import { useState, useEffect, useCallback } from "react";
import { Fade } from "@mui/material";
import NoticeAlert from "../components/Alerts/NoticeAlert";
import ActionAlert from "../components/Alerts/ActionAlert";

export const MSG_ALERT_IDS = {
  SLIDE_SYNC: "SLIDE_SYNC",
  JUMP_SLIDE_ONE: "JUMP_SLIDE_ONE",
  JUMP_SLIDE_GAMEPLAY: "JUMP_SLIDE_GAMEPLAY",
  LAST_SLIDE_CONTINUE_PLAY: "LAST_SLIDE_CONTINUE_PLAY",
  JUMPED_EARLIER_PLAY_SLIDE: "JUMPED_EARLIER_PLAY_SLIDE",
};

const MSG_ALERT_CONFIGS = {
  [MSG_ALERT_IDS.SLIDE_SYNC]: {
    AlertComponent: ActionAlert,
    msg: "Resuming where you left off.",
    btnText: "Go to Slide 1",
    priorityOver: { [MSG_ALERT_IDS.LAST_SLIDE_CONTINUE_PLAY]: true },
  },
  [MSG_ALERT_IDS.JUMP_SLIDE_ONE]: {
    AlertComponent: NoticeAlert,
    msg: "Jumped to slide 1.",
  },
  [MSG_ALERT_IDS.JUMP_SLIDE_GAMEPLAY]: {
    AlertComponent: NoticeAlert,
    msg: "Jumped to gameplay slide.",
    block: { [MSG_ALERT_IDS.SLIDE_SYNC]: true },
  },
  [MSG_ALERT_IDS.LAST_SLIDE_CONTINUE_PLAY]: {
    AlertComponent: ActionAlert,
    msg: "Want to continue playing?",
    btnText: "Unpause students",
    duration: -1,
  },
  [MSG_ALERT_IDS.JUMPED_EARLIER_PLAY_SLIDE]: {
    AlertComponent: NoticeAlert,
    msg: "Jumped to an earlier slide.",
    btnText: "Undo",
  },
};

export const MsgAlert = ({
  AlertComponent,
  popCurrMsgAlert,
  duration,
  ...otherProps
}) => {
  const [isFadingOut, setIsFadingOut] = useState(false);

  const { msg, btnText, onBtnClickHandler } = otherProps;
  const hasButton = btnText && onBtnClickHandler;
  const isValidMsg = !!(msg && AlertComponent);
  // if duration is falsy, choose default value based on whether theres a button,
  // negative duration values result in no timeout-based closing
  const closeTimeoutDuration = isValidMsg
    ? duration || (hasButton ? 5000 : 3000)
    : null;

  useEffect(() => {
    if (closeTimeoutDuration > 0) {
      const timeoutId = setTimeout(() => {
        setIsFadingOut(true);
      }, closeTimeoutDuration);

      return () => clearTimeout(timeoutId);
    }
  }, [closeTimeoutDuration]);

  return (
    isValidMsg && (
      <Fade
        in={isValidMsg && !isFadingOut}
        timeout={200}
        onExited={() => {
          setIsFadingOut(false);
          popCurrMsgAlert();
        }}
      >
        <div className="msg-alert-container">
          <AlertComponent
            {...otherProps}
            className="msg-alert"
            onCloseHandler={() => void setIsFadingOut(true)}
          />
        </div>
      </Fade>
    )
  );
};

const useMsgAlerts = (isSessionLoaded, currSlideNumber) => {
  const [alertQueue, setAlertQueue] = useState([]);

  const enqueueMsgAlert = useCallback((id, slideNumber, extraData = {}) => {
    const { priorityOver, block, ...newMsgAlertData } = {
      ...MSG_ALERT_CONFIGS[id],
      ...extraData,
      id,
      slideNumber,
    };
    setAlertQueue((prev) => {
      // ignore enqueue if any messages already in queue explicitly want to block the new message
      if (prev.some((aData) => MSG_ALERT_CONFIGS[aData.id]?.block?.[id])) {
        return prev;
      }
      let newAlertQueue = [...prev, newMsgAlertData];
      // filter out any messages already in queue that new message explicitly wants to block
      if (block) {
        newAlertQueue = newAlertQueue.filter((aData) => !block[aData.id]);
      }
      // sort list to ensure new message is placed ahead of any lower priority messages in the queue already
      if (priorityOver) {
        newAlertQueue.sort((aData) => (priorityOver[aData.id] ? 1 : -1));
      }

      return newAlertQueue;
    });
  }, []);

  const popCurrMsgAlert = useCallback(() => {
    setAlertQueue((prev) => prev.slice(1));
  }, []);

  // clear queue on slide changes, keep any alerts intended for current slide
  useEffect(() => {
    setAlertQueue((prev) =>
      prev.filter((aData) => aData.slideNumber === currSlideNumber)
    );
  }, [currSlideNumber]);

  return {
    currMsgAlertData: isSessionLoaded ? alertQueue[0] : null,
    enqueueMsgAlert,
    popCurrMsgAlert,
  };
};

export default useMsgAlerts;
